export default {
    title: 'Templates',
    createTemplate: 'Create Template',
    searchPlaceholder: 'Search templates...',
    showingResults: 'Showing {count} results',
    total: 'Total',
    sort: 'Sort',

    all: 'All',
    myTemplates: 'My Templates',
    favorites: 'Favorites',
    allRegions: 'All Regions',

    sortByUpdated: 'Recently Updated',
    sortByCreated: 'Recently Created',
    sortByNameAsc: 'Name (A-Z)',
    sortByNameDesc: 'Name (Z-A)',

    types: {
        PATENT_APPLICATION: 'Patent Application',
        INVENTION_DISCLOSURE: 'Invention Disclosure',
        OFFICE_ACTION_RESPONSE: 'Office Action Response',
    },

    regions: {
        all: 'All Regions',
        USPTO: 'USPTO',
        EPO: 'EPO',
        PCT: 'PCT',
        CNIPA: 'CNIPA',
        JPO: 'JPO',
        KIPO: 'KIPO',
    },

    technologyArea: {
        general: 'General',
        electrical: 'Electrical',
        mechanical: 'Mechanical',
        chemical: 'Chemical',
        software: 'Software',
        biotech: 'Biotech',
    },

    preview: 'Preview',
    edit: 'Edit',
    editInfo: 'Edit Info',
    editContent: 'Edit Content',
    duplicate: 'Duplicate',
    delete: 'Delete',
    favorite: 'Favorite',
    unfavorite: 'Unfavorite',
    setDefault: 'Set as Default',
    unsetDefault: 'Unset Default',
    defaultTemplate: 'Default Template',
    moreActions: 'More Actions',

    source: 'Source',
    system: 'System',
    custom: 'Custom',
    systemTemplate: 'System Template',

    name: 'Name',
    type: 'Type',
    region: 'Region',
    updated: 'Updated',
    actions: 'Actions',

    messages: {
        fetchListFailed: 'Failed to fetch templates',
        fetchDetailFailed: 'Failed to fetch template details',
        createSuccess: 'Template created successfully',
        createFailed: 'Failed to create template',
        updateSuccess: 'Template updated successfully',
        updateFailed: 'Failed to update template',
        deleteSuccess: 'Template deleted successfully',
        deleteFailed: 'Failed to delete template',
        duplicateSuccess: 'Template duplicated successfully',
        duplicateFailed: 'Failed to duplicate template',
        favoriteSuccess: 'Added to favorites',
        favoriteFailed: 'Failed to add to favorites',
        unfavoriteSuccess: 'Removed from favorites',
        unfavoriteFailed: 'Failed to remove from favorites',
        setDefaultSuccess: 'Default template set successfully',
        setDefaultFailed: 'Failed to set default template',
        unsetDefaultSuccess: 'Default template unset successfully',
        unsetDefaultFailed: 'Failed to unset default template',
    },

    noTemplates: 'No templates yet',
    noTemplatesDesc: 'Create your first template to get started',
    noResults: 'No matching templates found',
    noResultsDesc: 'Try adjusting your search or filters',

    dialog: {
        createTemplate: 'Create Template',
        createTemplateDesc: 'Select type and set name to create a professional template',
        templateName: 'Template Name',
        templateNamePlaceholder: 'Enter template name',
        templateType: 'Template Type',
        creationMethod: 'Creation Method',
        recommended: 'Recommended',
        blankTemplate: 'Blank Template',
        blankTemplateDesc: 'Start from scratch',
        copyFromExisting: 'Copy Template',
        copyFromExistingDesc: 'Based on existing template',
        selectBaseTemplate: 'Select Base Template',
        noTemplatesAvailable: 'No templates available',
        createAndEdit: 'Create & Edit',
        sections: 'Sections',
        cancel: 'Cancel',
        unsavedChangesTitle: 'Unsaved Changes',
        unsavedChangesMessage: 'You have unsaved changes. Are you sure you want to close? Your changes will be lost.',
        discardChanges: 'Discard Changes',
        continueEditing: 'Continue Editing',
        lastUpdated: 'Last Updated',
        noContent: 'No content',
        // Level selection (Super Admin only)
        level: 'Visibility',
        adminOnly: 'Admin Only',
        systemLevel: 'System Level',
        userLevel: 'User Level',
        systemLevelDesc: 'Visible to all users',
        userLevelDesc: 'Visible to me only',
        // Description
        description: 'Description',
        descriptionPlaceholder: 'Briefly describe the purpose and use case of this template...',
        optional: 'Optional',
        // Word import
        importFromWord: 'Import from Word',
        importFromWordDesc: 'Extract sections from .docx',
        uploadWordFile: 'Upload Word Document',
        uploadWordAction: 'Drop .docx file here or click to upload',
        uploadWordHint: 'Supports .docx files with heading-based sections',
        parsing: 'Parsing document...',
        parseSuccess: 'Successfully parsed {count} sections',
        parseFailed: 'Failed to parse document',
        parsedSections: 'Parsed Sections ({count})',
    },

    // Template Editor
    templateEditor: {
        back: 'Back',
        untitledTemplate: 'Untitled Template',
        clickToEdit: 'Click to edit',
        templateType: 'Template Type',
        region: 'Region',
        technologyArea: 'Technology Area',
        from: 'From',
        visibility: 'Visibility',
        private: 'Private',
        library: 'Library',
        saveTemplate: 'Save Template',
        aiInstructions: 'AI Instructions',
        aiInstructionsDesc: 'Define AI instructions for each section to guide content generation that matches your specific requirements.',
        addSection: 'Add Section',
        sectionTitle: 'Section Title',
        insert: 'Insert',
        delete: 'Delete',
        selectInstruction: 'Select Instruction',
        aiInstructionPlaceholder: 'Enter AI instruction for this section...',
        description: 'Description',
        descriptionPlaceholder: 'Describe the template purpose and key features...',
        // Level
        level: 'Visibility',
        // Collapse/Expand buttons
        collapseAll: 'Collapse All',
        expandAll: 'Expand All',
        // Section prompt editor
        aiInstruction: 'AI Instruction',
        chars: 'chars',
        fromLibrary: 'From Library',
        remove: 'Remove',
        required: 'Required',
        // Section names
        sections: {
            title: 'Title',
            crossReference: 'Cross-Reference to Related Applications',
            fieldOfInvention: 'Field of Invention',
            background: 'Background',
            summary: 'Summary',
            briefDescription: 'Brief Description of Figures',
            detailedDescription: 'Detailed Description',
            claims: 'Claims',
            abstract: 'Abstract',
            newSection: 'New Section',
        },
        types: {
            patentApplication: 'Patent Application',
            inventionDisclosure: 'Invention Disclosure',
            officeActionResponse: 'Office Action Response',
        },
        messages: {
            nameRequired: 'Please enter a template name',
            updateSuccess: 'Template updated successfully',
            saveSuccess: 'Template saved successfully',
            saveFailed: 'Failed to save template',
            loadFailed: 'Failed to load template',
            insertedPlaceholder: 'Inserted [{title}]',
        },
        systemTemplate: 'System Template',
        systemTemplateReadOnly: 'System templates are read-only',
        readOnly: 'Read Only',
        clickToEditName: 'Click to edit name',
    },
};
